import {
    Component,
    Input,
    HostBinding,
    forwardRef,
    input,
    output,
    effect,
    untracked,
    viewChild,
    DestroyRef,
    inject,
    computed,
    signal,
} from "@angular/core";
import {
    DropDownListComponent,
    DropDownSize,
    GroupTemplateDirective,
    HeaderTemplateDirective,
    ValueTemplateDirective,
    ItemTemplateDirective,
    NoDataTemplateDirective,
} from "@progress/kendo-angular-dropdowns";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { ITranslateObject } from "../../../views-interfaces";
import { WkTranslatePipe, NestedValuePipe } from "@shared/utils";
import { DropdownSearchService } from "../dropdown-search/dropdown-search.service";
import { DropdownSearchBoxComponent } from "../dropdown-search/dropdown-search-box.component";
import { DropdownPopupLayout, DROPDOWNLIST_POPUP_LAYOUT } from "../dropdown-popup/dropdown-popup-layout.service";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { HighlightMatchPipe } from "./highlight-match.pipe";
import { CloseOnAncestorScrollDirective } from "../../directives/close-on-ancestor-scroll/index";

export interface DropDownListWrapperChange<T = any> {
    target: { value: T };
}

@Component({
    selector: "kendo-dropdownlist-wrapper",
    templateUrl: "./kendo-dropdownlist-wrapper.component.html",
    host: {
        class: "wk-ddlw",
        "cg3-theme": "PDS-v3",
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => KendoDropDownListWrapper),
            multi: true,
        },
        WkTranslatePipe,
        NestedValuePipe,
        DropdownSearchService,
        DropdownPopupLayout,
    ],
    imports: [
        JumpstartComponentsModule,
        DropDownListComponent,
        GroupTemplateDirective,
        HeaderTemplateDirective,
        ValueTemplateDirective,
        ItemTemplateDirective,
        NoDataTemplateDirective,
        WkTranslatePipe,
        NestedValuePipe,
        HighlightMatchPipe,
        CloseOnAncestorScrollDirective,
        DropdownSearchBoxComponent,
    ],
})
class KendoDropDownListWrapper implements ControlValueAccessor {
    readonly size = input<DropDownSize>("medium");
    readonly textField = input("label");
    readonly valueField = input("id");
    readonly activeField = input("active");
    readonly flowClass = input("");
    readonly virtual = input(false);
    readonly changed = output<DropDownListWrapperChange>();
    readonly label = input<string | ITranslateObject>("");
    readonly valuePrimitive = input(true);
    readonly isGroupingEnabled = input(false);
    readonly tooltipIfDisabled = input("");
    readonly tooltipIfReadonly = input("");

    // Where Kendo mounts the popup; consumers inside overflow-clipped scroll containers pass "root" to escape the clip.
    readonly popupAppendTo = input<"root" | "component">("component");

    // Shown when no value is selected; mirrors the multiselect wrapper's `placeholder` input.
    readonly placeholder = input("");

    // Id/key corresponding to empty value to load into the list instead of the placeholder.
    readonly emptyValue = input<unknown>(null);

    // Indicates whether the list has an empty option, which affects the rendering of the placeholder.
    private readonly hasEmptyOption = computed(() => this.findByValueField(this.emptyValue()) !== undefined);

    protected readonly defaultItem = computed(() =>
        this.hasEmptyOption()
            ? null
            : {
                  [this.valueField()]: null,
                  [this.textField()]: this.placeholder() || "General.None",
              },
    );

    // Search: opt-in per consumer. Defaults keep every current usage unchanged.
    readonly filterable = input(false);

    protected readonly search = inject(DropdownSearchService);

    // Option list. null / undefined collapse to an empty array so every read downstream is safe.
    readonly data = input<any[], any[] | null | undefined>([], { transform: (value) => value ?? [] });

    private static nextId = 0;
    protected readonly hostId = `wk-ddlw-${KendoDropDownListWrapper.nextId++}`;

    @HostBinding("attr.id")
    protected get hostIdAttr(): string {
        return this.hostId;
    }

    private _disabled = false;
    private _inputDisabled = false;
    @Input()
    set disabled(val: boolean) {
        this._inputDisabled = val;
        this.setDisabledState(val);
    }
    get disabled() {
        return this._disabled;
    }

    private _readonly = false;
    private _inputReadonly = false;
    @Input()
    set readonly(val: boolean) {
        this._inputReadonly = val;
        this.setReadonlyState(val);
    }
    get readonly() {
        return this._readonly;
    }

    private setReadonlyState(isReadonly: boolean): void {
        this._readonly = isReadonly || this._inputReadonly;
        this.applyReadonlyState();
    }

    private applyReadonlyState(): void {
        if (this.inputElement) {
            this.inputElement.setAttribute("readonly", String(this._readonly));
            this.inputElement.setAttribute("aria-readonly", String(this._readonly));
        }
    }

    private readonly dropdownListRef = viewChild<DropDownListComponent>("dropdownList");
    private dropdownList!: DropDownListComponent;
    private readonly searchBox = viewChild(DropdownSearchBoxComponent);
    private readonly popupLayout = inject(DropdownPopupLayout);
    private readonly destroyRef = inject(DestroyRef);
    private inputElement: HTMLElement | null = null;

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.inputElement = null;
            this.detachPopupHoverListener();
        });

        this.search.connect({
            data: this.data,
            textField: this.textField,
            filterable: this.filterable,
        });

        this.popupLayout.connect({
            layout: DROPDOWNLIST_POPUP_LAYOUT,
            stops: { search: () => this.searchBox()?.stops() ?? [] },
            field: () => this.dropdownList?.wrapper?.nativeElement,
        });

        // Wire up the inner dropdownlist once the view query resolves it (runs once).
        effect(() => {
            const ddl = this.dropdownListRef();
            if (ddl && this.dropdownList == null) {
                untracked(() => {
                    this.dropdownList = ddl;
                    this.updateInputElement();
                    this.applyDisabledState();
                    this.applyReadonlyState();
                });
            }
        });
    }

    private updateInputElement(): void {
        if (this.dropdownList?.wrapper) {
            this.inputElement = this.dropdownList.wrapper.nativeElement.querySelector(".k-input-inner");
            if (this.inputElement) {
                this.inputElement.dataset.input = "true";
                this.inputElement.setAttribute("disabled", String(this._disabled));
                this.inputElement.setAttribute("readonly", String(this._readonly));
                this.inputElement.setAttribute("aria-disabled", String(this._disabled));
                this.inputElement.setAttribute("aria-readonly", String(this._readonly));
            }
        }
    }

    private applyDisabledState(): void {
        if (this.inputElement) {
            this.inputElement.setAttribute("disabled", String(this._disabled));
            this.inputElement.setAttribute("aria-disabled", String(this._disabled));
        }
    }

    protected onItemDisabled = (args: { dataItem: any; index: number }): boolean => {
        const item = args.dataItem;
        const activeField = this.activeField();
        if (!activeField || !Object.keys(item).includes(activeField)) return false;

        return item[this.valueField()] !== this.value && !item[activeField];
    };

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._disabled = isDisabled || this._inputDisabled;
        this.applyDisabledState();
    }

    protected handleValueChange(newValue: any): void {
        const item = this.findByValueField(newValue);
        if (item && "disabled" in item && item.disabled) {
            newValue = this.value;
        }
        this.value = newValue;
        if (this.dropdownList) {
            this.dropdownList.value = this.displayValue(newValue);
        }
        this.onChange(newValue);
        this.changed.emit({ target: { value: newValue } });
    }

    protected displayValue(value: unknown): unknown {
        return value == null || value === "" ? this.emptyValue() : value;
    }

    private findByValueField(value: any): any {
        const keys = this.valueField().split(".");

        return this.data().find((item) => {
            let current: any = item;
            for (const key of keys) {
                if (current && key in current) {
                    current = current[key];
                } else {
                    return false;
                }
            }
            return current === value;
        });
    }

    private onChange: (value: any) => void = () => {};
    protected onTouched: () => void = () => {};
    private _value: any = null;
    @Input()
    set value(val: any) {
        this.writeValue(val);
    }
    get value() {
        return this._value;
    }

    writeValue(obj: any): void {
        this._value = obj;
        if (this.dropdownList) {
            this.dropdownList.value = this.displayValue(obj);
        }
    }

    @HostBinding("class")
    protected get hostClasses(): string {
        const classes = [];

        if (this.flowClass()) {
            classes.push(this.flowClass());
        }

        return classes.join(" ");
    }

    @HostBinding("class.wk-ddlw--open")
    protected get hostOpenClass() {
        return this.isOpen;
    }

    protected isOpen = false;

    protected onSearchNavigate(event: KeyboardEvent): void {
        this.dropdownList?.keydown(event);
    }

    // Kendo only tracks focus on its field and its own filter input, so the search box reports in as that input.
    protected onSearchFocus(): void {
        this.dropdownList?.onFilterFocus();
    }

    protected onSearchBlur(): void {
        this.dropdownList?.blurFilterInput();
    }

    // Tab order inside the open popup comes from the layout.
    protected onSearchTab(event: KeyboardEvent): void {
        if (!this.popupLayout.handleTab(event)) return;

        // Focus moves first, so a Tab stepping out of the dropdown carries on from the field once the popup is gone.
        this.dropdownList.wrapper.nativeElement.focus({ preventScroll: true });
        this.dropdownList.togglePopup(false);
    }

    // The field may be clipped by a scroll-container ancestor, so scroll to it before Kendo positions the popup.
    protected onBeforeOpen(): void {
        this.dropdownList?.wrapper?.nativeElement?.scrollIntoView({ block: "nearest", inline: "nearest" });
    }

    // The hover attacher is a no-op unless the popup is appended to the root, so it is called unconditionally.
    protected onOpen(): void {
        this.isOpen = true;

        queueMicrotask(() => this.attachPopupHoverListener());
        // Kendo positions (and possibly flips) the popup after this event.
        setTimeout(() => {
            const popup = this.dropdownList?.popupRef?.popupElement;
            if (popup) this.popupLayout.attach(popup);
        }, 0);
    }

    protected onClose(): void {
        // Closing removes the search box; keep focus on the field rather than dropping it to the body.
        const popup = this.dropdownList?.popupRef?.popupElement;
        if (popup?.contains(document.activeElement)) this.dropdownList.wrapper.nativeElement.focus({ preventScroll: true });

        this.isOpen = false;
        this.detachPopupHoverListener();
        this.search.reset(); // immediate list reset + flush of any pending debounced term
    }

    // Appended to the document root the popup sits outside this component, so the hover handler bound
    // in the template never sees the options - the popup gets its own.
    private popupEl: HTMLElement | null = null;
    private readonly popupHoverListener = (event: Event): void => this.onHover(event);

    private attachPopupHoverListener(): void {
        if (this.popupAppendTo() !== "root") return;

        const popup = this.dropdownList?.popupRef?.popupElement;
        if (!popup || popup === this.popupEl) return;
        this.detachPopupHoverListener();

        this.popupEl = popup;
        popup.addEventListener("mouseover", this.popupHoverListener);
    }

    private detachPopupHoverListener(): void {
        this.popupEl?.removeEventListener("mouseover", this.popupHoverListener);
        this.popupEl = null;
    }

    @HostBinding("attr.size")
    protected get hostSize() {
        return this.size();
    }

    // Hovering a row measures its label. When the label is clipped, mounts the tooltip targeting its id.
    // A row is an option in the popup, or the field itself, which holds the selected value.
    private static readonly TRUNCATION_ROW_SELECTOR = "li.k-list-item, kendo-dropdownlist";
    private static readonly TRUNCATION_LABEL_SELECTOR = ".dropdownlist-wrapper-value, .dropdownlist-wrapper-item";

    // Stores ids of the labels measured as clipped: only those get a tooltip.
    private readonly clippedLabelIds = signal<ReadonlySet<string>>(new Set());

    protected readonly valueLabelId = `${this.hostId}-value`;

    // Option ids are handed out in render order and cached per data item: the item template exposes no
    // index, and the value field is neither guaranteed to be present nor unique.
    private readonly optionLabelIds = new Map<unknown, string>();

    protected optionLabelId(dataItem: unknown): string {
        let labelId = this.optionLabelIds.get(dataItem);
        if (!labelId) {
            labelId = `${this.hostId}-option-${this.optionLabelIds.size}`;
            this.optionLabelIds.set(dataItem, labelId);
        }

        return labelId;
    }

    protected isClipped(labelId: string): boolean {
        return this.clippedLabelIds().has(labelId);
    }

    // Measures the hovered row's label and stores its id if clipped/truncated
    protected onHover(event: Event): void {
        const row = (event.target as HTMLElement | null)?.closest<HTMLElement>(KendoDropDownListWrapper.TRUNCATION_ROW_SELECTOR);
        const label = row?.querySelector<HTMLElement>(KendoDropDownListWrapper.TRUNCATION_LABEL_SELECTOR);
        if (!label?.id) return;

        const clipped = label.scrollWidth > label.clientWidth || label.scrollHeight > label.clientHeight;
        this.clippedLabelIds.update((ids) => {
            if (ids.has(label.id) === clipped) return ids;

            const next = new Set(ids);
            if (clipped) next.add(label.id);
            else next.delete(label.id);

            return next;
        });
    }
}

export { KendoDropDownListWrapper };
